import React, { useEffect } from "react";
import { EXPERIENCES } from "../data/experiences";
import { ExperienceCard } from "./ExperiencesPage";
import { Ornament, SectionHeader } from "./Ornament";
import Seo from "./Seo";

const CONCEPT_STORE_SLUGS = [
  "creative-luxury-bars",
  "signature-installations",
  "creative-costumes",
  "creative-luxury-catering",
];
const BASE = "https://lumiereevents.org";

const useTop = () => {
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
};

export default function ConceptStorePage() {
  useTop();
  const items = EXPERIENCES.filter((e) => CONCEPT_STORE_SLUGS.includes(e.slug));

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Concept Store — Lumière Entertainment",
    numberOfItems: items.length,
    itemListElement: items.map((e, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Product",
        name: e.name,
        description: e.desc,
        category: e.subtitle,
        image: e.image && e.image.startsWith("http") ? e.image : BASE + (e.image || "/generated/hero.jpg"),
        brand: { "@id": BASE + "/#org" },
        offers: {
          "@type": "Offer",
          availability: "https://schema.org/InStock",
          priceCurrency: "EUR",
          url: BASE + "/contact",
        },
      },
    })),
  };

  return (
    <div className="pt-32 bg-[var(--lu-ivory)]" data-testid="concept-store-page">
      <Seo
        path="/concept-store"
        title="Concept Store & Creative Costumes — Luxury Bars, Installations, Bespoke Costumes | Lumière Entertainment"
        description="Curated concept store by Lumière Entertainment: Creative Luxury Bars, Signature Installations and Creative Costumes (bespoke show costumes, feather headpieces, LED wear, aerial wardrobe) for luxury weddings, brand launches, hotels, fashion events and private celebrations."
        keywords="luxury bar concepts, event bars Athens, signature installations, floral installations, creative costumes, bespoke show costumes, feather headpieces, LED costumes, aerial silk costumes, event styling Greece, statement backdrops"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Concept Store & Creative Costumes", path: "/concept-store" },
        ]}
        jsonLd={jsonLd}
      />

      <section className="container-lux text-center pb-4">
        <p className="kicker text-[var(--lu-gold-dark)]">Curated Collection</p>
        <h1 className="mt-6 h-editorial text-[46px] sm:text-[64px] lg:text-[92px] text-[var(--lu-black)] leading-[0.98]">
          Concept Store &amp;{" "}
          <span className="italic text-[var(--lu-gold-dark)]">Creative Costumes</span>
        </h1>
        <Ornament className="my-10" />
        <p className="text-[19px] font-serif italic text-[var(--lu-gold-dark)] max-w-2xl mx-auto">
          Bespoke bars, installations &amp; couture-grade costumes — ready to be
          adapted to your event.
        </p>
        <p className="mt-6 text-[15px] leading-[1.9] text-black-70 font-light max-w-3xl mx-auto">Signature bar concepts, large-scale installations and bespoke costumes
          designs that define the atmosphere of every event — from welcome moments
          to the most photographed spot of the evening.</p>
      </section>

      <section className="section-y">
        <div className="container-lux">
          {items.map((item, i) => (
            <ExperienceCard key={item.slug} item={item} index={i} />
          ))}
        </div>
      </section>

      <section className="section-y bg-[var(--lu-black)] grain">
        <div className="container-lux text-center">
          <SectionHeader
            kicker="Bespoke Only"
            title="The concept that fits"
            italicTitle="on your own evening."
            light
          />
          <p className="mt-8 text-[15px] leading-[1.9] text-ivory-75 font-light max-w-2xl mx-auto">Each bar and installation is fully customizable to the colors, the
            venue and the flow of your event. Contact for a custom brief
            and pricing.</p>
          <div className="mt-12 flex flex-col sm:flex-row gap-5 justify-center">
            <a
              href="/contact"
              className="btn-gold"
              data-testid="concept-store-cta-proposal"
            >Ask for Bespoke Concept</a>
            <a
              href="/experiences"
              className="btn-gold-outline"
              data-testid="concept-store-cta-experiences"
            >See all Experiences</a>
          </div>
        </div>
      </section>
    </div>
  );
}
